import React, {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import authenticateService from '../services/authenticateService';
import { DEV_DOMAIN_URL_LOGIN } from '../environment';
import { clearSessionStorage } from '../services/session-storage-service';
import { AwsLoginErrors, AuthAttribute } from '../constants/telosair-interfaces';
import axiosInstance from '../_helpers/interceptor';

type AuthenticationContextProps = {
  authUser: string | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<AuthAttribute>;
  isLoggedIn: () => void;
  setAuthUser: (data: string) => void;
  logout: () => void;
};
type AuthenticationProviderProps = { children: React.ReactNode };
const AuthenticationContext = createContext({} as AuthenticationContextProps);

/**
 * AuthenticationProvider component.
 * @param props - Properties of AuthenticationProvider.
 * @returns - Returns ReactElement.
 */
export function AuthenticationProvider(props: AuthenticationProviderProps): ReactElement {
  const { children } = props;
  const [authUser, setAuthUser] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const isLoggedIn = useCallback(async () => {
    const sessionUser = sessionStorage.getItem('token');
    if (sessionUser) {
      try {
        const sessionUserData = sessionUser;
        setAuthUser(sessionUserData);
        setLoading(false);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  /**
   * Logout function.
   */
  const logout = (): void => {
    try {
      setAuthUser(null);
      clearSessionStorage();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  useEffect(() => {
    isLoggedIn();
  }, []);

  /**
   * Logs in to the application server after successful Cognito authentication.
   * @param userName - Username.
   * @param password - Password.
   * @returns - Callback.
   */
  async function loginToApplication(userName: string, password: string): Promise<AuthAttribute> {
    const data = { userName, password };
    try {
      const responseData = await axiosInstance.post(`${DEV_DOMAIN_URL_LOGIN}/custom/login/`, data);
      if (userName) {
        if (responseData.status === 200) {
          sessionStorage.setItem('role', responseData.data.role);
          sessionStorage.setItem('client_name', responseData.data.clientName);
          sessionStorage.setItem('user_name', userName);
          const filterDataTemp = {
            clients: [],
            facility: [],
            zones: [],
            rooms: [],
            sensors: [],
            selectedTime: '24hrs',
            comparisionOption: 'Particle Concentration',
            floorRangeType: 'Temperature',
            timeSeries: ['particleConcentration'],
            timeSeriesType: 'pn03_m',
            detailed: [],
            selectedserial_number: 0
          };
          sessionStorage.setItem('filters', JSON.stringify(filterDataTemp));
          sessionStorage.setItem('timeTrendsFilters', JSON.stringify(filterDataTemp));
          return { error: false };
        }
      }
      return { error: AwsLoginErrors.other };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      return { error: AwsLoginErrors.other };
    }
  }

  /**
   * Logs in the user using Cognito authentication and then logs in to the application server.
   * @param username - The username of the user.
   * @param password - The password of the user.
   * @returns - Callback.
   */
  async function login(username: string, password: string): Promise<AuthAttribute> {
    const response = await authenticateService.login(username, password);
    if (response.error) {
      return response;
    }
    const customeLoginResponse = await loginToApplication(username, password);
    return customeLoginResponse;
  }

  const AuthenticationProviderValue = useMemo(
    () => ({ authUser, loading, login, isLoggedIn, setAuthUser, logout }),
    [authUser, loading, login, isLoggedIn, setAuthUser, logout]
  );

  // if (loading) {
  //   return <Loader />;
  // }

  return (
    <AuthenticationContext.Provider value={AuthenticationProviderValue}>
      {children}
    </AuthenticationContext.Provider>
  );
}

/**
 * Custom hook to use the authenticationContext and access authUser and loading state.
 * @returns - AuthenticationContext state.
 */
export const useAuth = (): AuthenticationContextProps => useContext(AuthenticationContext);
